(function () {
  /**
   * CanvasArtPlus — Optimized Hide "Frame" option group when ONLY available choice is
   * "No Frame" or "Print Only" or "N/A".
   *
   * Optimizations:
   * - Throttled apply() (runs at most once per animation frame)
   * - Cheap availability checks first (disabled/aria/class)
   * - Expensive visibility checks only if needed
   * - Cache frame fieldset
   */

  const DEBUG = false;

  const HIDE_IF_ONLY_VALUE_IS = new Set([
    "no frame",
    "noframe",
    "print only",
    "printonly",
    "unframed",
    "none",
    "n/a",
    "na"
  ]);

  const norm = (s) => String(s || "").toLowerCase().trim().replace(/\s+/g, " ");
  const normTight = (s) => norm(s).replace(/\s/g, "");
  const isNoFrameMeaning = (v) => HIDE_IF_ONLY_VALUE_IS.has(norm(v)) || HIDE_IF_ONLY_VALUE_IS.has(normTight(v));

  // Cached DOM node
  let frameFs = null;

  function findFrameFieldset() {
    if (frameFs && document.contains(frameFs)) return frameFs;
    frameFs = document.querySelector('fieldset[data-fieldset-index="2"]');
    return frameFs;
  }

  function isDisabledByWrapping(el) {
    if (!el) return false;
    const cls = (el.className || "").toString();
    if (/\bdisabled\b|\bis-disabled\b|\bunavailable\b/.test(cls)) return true;
    const aria = el.getAttribute && el.getAttribute("aria-disabled");
    if (aria && aria.toLowerCase() === "true") return true;
    if (el.getAttribute && el.getAttribute("data-disabled") === "true") return true;
    return false;
  }

  // Expensive, use sparingly
  function isVisibleFallback(el) {
    if (!el) return false;
    if (el.hidden) return false;
    const aria = el.getAttribute && el.getAttribute("aria-hidden");
    if (aria && aria.toLowerCase() === "true") return false;

    const cs = window.getComputedStyle(el);
    if (!cs || cs.display === "none" || cs.visibility === "hidden") return false;

    // This can trigger layout, so only used as fallback
    const rects = el.getClientRects();
    return !!(rects && rects.length);
  }

  function getAvailableValues(frameFsEl) {
    if (!frameFsEl) return [];

    const labels = frameFsEl.querySelectorAll("label");
    const values = [];

    // First pass: cheap checks only
    for (const label of labels) {
      const input = label.querySelector('input[type="radio"]');
      if (!input) continue;

      // If input disabled, skip
      if (input.disabled) continue;

      const aria = input.getAttribute("aria-disabled");
      if (aria && aria.toLowerCase() === "true") continue;

      // If label/parent marked disabled, skip
      if (isDisabledByWrapping(label) || isDisabledByWrapping(label.parentElement) || isDisabledByWrapping(label.closest("li"))) continue;

      const val = (input.value || "").trim();
      if (!val) continue;

      values.push(val);
    }

    // If we found more than 1 value, we can skip visibility checks
    // because it clearly isn't "No Frame only".
    const uniq = Array.from(new Set(values));
    if (uniq.length !== 1) return uniq;

    // If uniq is 1, we must confirm we didn't accidentally include hidden labels.
    // Do a second pass using visibility fallback, but only until we find a 2nd value.
    const safeValues = [];
    for (const label of labels) {
      const input = label.querySelector('input[type="radio"]');
      if (!input) continue;

      if (input.disabled) continue;
      const aria = input.getAttribute("aria-disabled");
      if (aria && aria.toLowerCase() === "true") continue;

      if (isDisabledByWrapping(label) || isDisabledByWrapping(label.parentElement) || isDisabledByWrapping(label.closest("li"))) continue;

      // Only now check visibility (expensive)
      if (!isVisibleFallback(label)) continue;

      const val = (input.value || "").trim();
      if (!val) continue;

      safeValues.push(val);

      const u = Array.from(new Set(safeValues));
      if (u.length > 1) return u; // early exit
    }

    return Array.from(new Set(safeValues));
  }

  function setHidden(el, hidden) {
    if (!el) return;
    el.style.display = hidden ? "none" : "";
    if (hidden) el.setAttribute("aria-hidden", "true");
    else el.removeAttribute("aria-hidden");
  }

  function applyNow() {
    const fs = findFrameFieldset();
    if (!fs) return;

    const avail = getAvailableValues(fs);
    const shouldHide = (avail.length === 1 && isNoFrameMeaning(avail[0]));

    if (DEBUG) {
      console.log("[HideFrame OPT] avail:", avail, "hide:", shouldHide);
    }

    setHidden(fs, shouldHide);
  }

  // Throttle: run once per frame even if called many times
  let scheduled = false;
  function apply() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      applyNow();
    });
  }

  function patchVariantPicker(vp) {
    if (vp.__capHideFramePatched) return;
    vp.__capHideFramePatched = true;

    const original = vp.updateVariantPicker?.bind(vp);
    if (typeof original !== "function") return;

    vp.updateVariantPicker = async function (...args) {
      const res = await original(...args);
      apply();
      return res;
    };

    // Extra safety: run on option changes (throttled)
    document.addEventListener("change", (e) => {
      const t = e.target;
      if (t && t.closest && t.closest("variant-picker")) apply();
    });

    apply();
  }

  function init() {
    const vp = document.querySelector("variant-picker");
    if (vp && typeof vp.updateVariantPicker === "function") {
      patchVariantPicker(vp);
      return;
    }

    const observer = new MutationObserver(function () {
      const vp2 = document.querySelector("variant-picker");
      if (vp2 && typeof vp2.updateVariantPicker === "function") {
        observer.disconnect();
        patchVariantPicker(vp2);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (customElements.get("variant-picker")) init();
  else customElements.whenDefined("variant-picker").then(init);
})();